import { AxiosError, AxiosResponse } from "axios"
import { QueryClient } from "react-query"
import { IClassStoreItem } from "ts/interfaces/Class"

import { IStoreChargeStudent } from "./SendStoreChargeDialog.types"

export interface ICartLine {
  item: IClassStoreItem
  quantity: number
}

// Backend cart caps: units per line and lines per request.
export const MAX_QUANTITY_PER_LINE = 500
export const MAX_CART_LINES = 20

export const isItemOutOfStock = (item: IClassStoreItem) =>
  item.out_of_stock || item.quantity_in_stock === 0

export const addToCart = (
  cart: ICartLine[],
  item: IClassStoreItem,
): ICartLine[] => {
  const line = cart.find((cartLine) => cartLine.item.id === item.id)
  if (!line) {
    return cart.length >= MAX_CART_LINES
      ? cart
      : [...cart, { item, quantity: 1 }]
  }
  if (line.quantity >= MAX_QUANTITY_PER_LINE) return cart
  return cart.map((cartLine) =>
    cartLine === line ? { ...line, quantity: line.quantity + 1 } : cartLine,
  )
}

export const decrementInCart = (
  cart: ICartLine[],
  itemId: number,
): ICartLine[] =>
  cart.flatMap((line) =>
    line.item.id !== itemId
      ? [line]
      : line.quantity > 1
        ? [{ ...line, quantity: line.quantity - 1 }]
        : [],
  )

export const removeFromCart = (
  cart: ICartLine[],
  itemId: number,
): ICartLine[] => cart.filter((line) => line.item.id !== itemId)

export const filterStoreItems = (items: IClassStoreItem[], query: string) => {
  const term = query.trim().toLowerCase()
  return term
    ? items.filter((item) => item.name.toLowerCase().includes(term))
    : items
}

export const countAvailableItems = (items: IClassStoreItem[]) =>
  items.filter((item) => !isItemOutOfStock(item)).length

export type LineStock =
  | { kind: "unlimited" }
  | { kind: "ok"; remaining: number }
  | { kind: "short"; available: number; required: number }
  | { kind: "outOfStock" }

// Review stock per line against the predicted-charged count; N = 0 charges nobody, so nothing runs short.
export const getLineStock = (
  item: IClassStoreItem,
  quantity: number,
  chargedCount: number,
): LineStock => {
  if (chargedCount > 0 && isItemOutOfStock(item)) return { kind: "outOfStock" }
  if (item.quantity_in_stock == null) return { kind: "unlimited" }
  const required = quantity * chargedCount
  const remaining = item.quantity_in_stock - required
  return remaining < 0
    ? { kind: "short", available: item.quantity_in_stock, required }
    : { kind: "ok", remaining }
}

export const roundToCents = (value: number) => Math.round(value * 100) / 100

export const getLineTotal = ({ item, quantity }: ICartLine) =>
  roundToCents(item.price * quantity)

export const getCartCostPerStudent = (cart: ICartLine[]) =>
  roundToCents(
    cart.reduce((sum, line) => sum + line.item.price * line.quantity, 0),
  )

export const toPurchaseItems = (cart: ICartLine[]) =>
  cart.map(({ item, quantity }) => ({ store_item_id: item.id, quantity }))

export const getCartAnalytics = (cart: ICartLine[]) => ({
  line_count: cart.length,
  units_per_student: cart.reduce((sum, line) => sum + line.quantity, 0),
  item_ids: cart.map((line) => line.item.id),
  cost_per_student: getCartCostPerStudent(cart),
})

export interface IBalanceCheckRow {
  student: IStoreChargeStudent
  skipped: boolean
  // Spendable balance the prediction used — the Review table shows this, not `balance`.
  balance: number
  balanceAfter: number
}

// Checking plus pending, raw: pending outflows (fines) lower it, pending inflows (paychecks) raise it.
export const getSpendableBalance = (student: IStoreChargeStudent) =>
  roundToCents(student.booked_balance + student.pending_balance)

// Advisory only — backend skips anyone who can't afford the whole cart; 201 skipped_student_ids can differ.
export const getBalanceCheck = (
  students: IStoreChargeStudent[],
  perStudentCost: number,
) => {
  const rows: IBalanceCheckRow[] = students.map((student) => {
    const balance = getSpendableBalance(student)
    const skipped = balance < perStudentCost
    return {
      student,
      skipped,
      balance,
      balanceAfter: skipped ? balance : roundToCents(balance - perStudentCost),
    }
  })
  return {
    rows,
    chargedCount: rows.filter((row) => !row.skipped).length,
  }
}

export type BalanceCheck = ReturnType<typeof getBalanceCheck>

const EXCLUDED_PREVIEW_LIMIT = 5

// `+N more` counts from the id total — excluded rows on unloaded pages have no names.
export const getExcludedPreview = (
  excludedStudents: IStoreChargeStudent[],
  excludedCount: number,
) => {
  const names = excludedStudents
    .slice(0, EXCLUDED_PREVIEW_LIMIT)
    .map(formatStudentName)
  return { names, moreCount: Math.max(0, excludedCount - names.length) }
}

interface IStudentName {
  first_name: string
  last_name: string
}

export const formatStudentName = ({ first_name, last_name }: IStudentName) =>
  `${first_name} ${last_name}`.trim()

type StockConflictReason = "OUT_OF_STOCK" | "INSUFFICIENT_STOCK"

export interface IStockConflictLine {
  store_item_id: number
  reason: StockConflictReason
  required: number | null
  available: number | null
}

const asRecord = (value: unknown) =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined

const asNumber = (value: unknown) => (typeof value === "number" ? value : null)

const isConflictReason = (value: unknown): value is StockConflictReason =>
  value === "OUT_OF_STOCK" || value === "INSUFFICIENT_STOCK"

// 409 body shape is unconfirmed (O2): bare or `detail`-wrapped, reason per line or top-level.
// Any line we can't patch → null, and the caller refetches instead of guessing.
export const parseStockConflict = (
  error: AxiosError,
): IStockConflictLine[] | null => {
  if (error.response?.status !== 409) return null
  const body = asRecord(error.response.data)
  const nested = asRecord(body?.detail) ?? body
  const rawLines = nested?.items
  if (!Array.isArray(rawLines) || rawLines.length === 0) return null

  const lines: IStockConflictLine[] = []
  for (const rawLine of rawLines) {
    const line = asRecord(rawLine)
    const storeItemId = asNumber(line?.store_item_id)
    const reason = line?.reason ?? nested?.reason
    const available = asNumber(line?.available)
    if (storeItemId === null || !isConflictReason(reason)) return null
    if (reason === "INSUFFICIENT_STOCK" && available === null) return null
    lines.push({
      store_item_id: storeItemId,
      reason,
      required: asNumber(line?.required),
      available,
    })
  }
  return lines
}

const patchCachedStoreItems = (
  queryClient: QueryClient,
  classId: number,
  patch: (item: IClassStoreItem) => IClassStoreItem,
) =>
  queryClient
    .getQueryCache()
    .findAll(["classesStoreItems", classId])
    .forEach((query) => {
      const prev = queryClient.getQueryData<AxiosResponse<IClassStoreItem[]>>(
        query.queryKey,
      )
      if (!prev) return

      queryClient.setQueryData<AxiosResponse<IClassStoreItem[]>>(
        query.queryKey,
        { ...prev, data: prev.data.map(patch) },
      )
    })

// 409 means nothing was charged: write what the backend says is left so Review shows those lines short.
export const patchStoreItemsConflict = (
  queryClient: QueryClient,
  classId: number,
  lines: IStockConflictLine[],
) => {
  const lineById = new Map(lines.map((line) => [line.store_item_id, line]))
  patchCachedStoreItems(queryClient, classId, (item) => {
    const line = lineById.get(item.id)
    if (!line) return item
    return line.reason === "OUT_OF_STOCK"
      ? { ...item, out_of_stock: true }
      : { ...item, quantity_in_stock: line.available }
  })
}

interface IStockLine {
  store_item_id: number
  quantity_in_stock?: number | null
}

// Write 201 stock into every cached classesStoreItems list for this class (no refetch).
// `null` = untracked, still written; a line without the key leaves its item untouched.
export const patchStoreItemsStock = (
  queryClient: QueryClient,
  classId: number,
  lines: IStockLine[],
) => {
  const stockById = new Map<number, number | null>()
  lines.forEach((line) => {
    if (line.quantity_in_stock !== undefined)
      stockById.set(line.store_item_id, line.quantity_in_stock)
  })
  if (stockById.size === 0) return

  patchCachedStoreItems(queryClient, classId, (item) =>
    stockById.has(item.id)
      ? { ...item, quantity_in_stock: stockById.get(item.id) ?? null }
      : item,
  )
}
